import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Upload, X, CheckCircle2, Loader2, Search, Share2, Gift, ShoppingBag, Wallet, ChevronDown, User, RefreshCw, ExternalLink, Copy, Download, Table as TableIcon, Database, ArrowUp, ArrowDown, ArrowUpDown, FileText, ListChecks, Check } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { NumberInput } from '../components/ui/number-input';
import { copyToClipboard } from '../lib/clipboard';
import { computeDuplicateIds } from '../lib/duplicateContacts';
import { TierBadge } from '../components/TierBadge';

const uploadImage = async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post('/uploads/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return data.url;
};

const STATUS_OPTIONS = ['draft', 'active', 'completed'];
const STAGE_FILTERS = [
  { key: 'contacted', label: 'contacted' },
  { key: 'address_received', label: 'address received' },
  { key: 'product_sent', label: 'product sent' },
  { key: 'product_received', label: 'product received' },
  { key: 'content_posted', label: 'posted' },
];

// Kanban grid columns — "to contact" first, then the same progression as STAGE_FILTERS.
const KANBAN_COLUMNS = [
  { key: 'to_contact', label: 'to be contacted', color: '#94a3b8' },
  { key: 'contacted', label: 'contacted', color: '#d394ab' },
  { key: 'address_received', label: 'address received', color: '#c98aa0' },
  { key: 'product_sent', label: 'product sent', color: '#b16fa3' },
  { key: 'product_received', label: 'product received', color: '#a35fb0' },
  { key: 'content_posted', label: 'posted', color: '#5fae8f' },
];
const KANBAN_FLAG_ORDER = ['contacted', 'address_received', 'product_sent', 'product_received', 'content_posted'];
// A creator's kanban column = the furthest-along stage flag that's currently true.
const kanbanStageOf = (c) => {
  for (let i = KANBAN_FLAG_ORDER.length - 1; i >= 0; i--) {
    if (c[KANBAN_FLAG_ORDER[i]]) return KANBAN_FLAG_ORDER[i];
  }
  return 'to_contact';
};
// Dropping a card on a column sets every flag up to and including that stage true, and
// resets everything after it — keeps the boolean progression internally consistent.
const kanbanFlagsForColumn = (colKey) => {
  const idx = KANBAN_FLAG_ORDER.indexOf(colKey);
  const patch = {};
  KANBAN_FLAG_ORDER.forEach((f, i) => { patch[f] = i <= idx; });
  return patch;
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest text-slate-400 font-medium">{label}</label>
    <div className="mt-0.5">{children}</div>
  </div>
);

// Open-in-new-tab + copy buttons for a link cell — only shown once there's an actual url.
const LinkActions = ({ url, testId }) => {
  if (!url || !/^https?:\/\//i.test(url)) return null;
  return (
    <div className="flex items-center gap-0.5 shrink-0">
      <a href={url} target="_blank" rel="noopener noreferrer" className="p-1 text-slate-400 hover:text-pink-600" title="open in new tab" data-testid={`${testId}-open`}>
        <ExternalLink className="w-3.5 h-3.5" />
      </a>
      <button
        type="button"
        onClick={async () => { const ok = await copyToClipboard(url); toast[ok ? 'success' : 'error'](ok ? 'link copied' : 'copy failed'); }}
        className="p-1 text-slate-400 hover:text-pink-600"
        title="copy link"
        data-testid={`${testId}-copy`}
      >
        <Copy className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

const ToggleChip = ({ label, checked, onChange, testId }) => (
  <label className="inline-flex items-center gap-1.5 cursor-pointer" data-testid={testId}>
    <Switch checked={checked} onCheckedChange={onChange} />
    <span className={`text-xs ${checked ? 'text-slate-800 font-medium' : 'text-slate-500'}`}>{label}</span>
  </label>
);

// Compact collapsible list of declined/backup creators — excluded from the campaign's
// main totals, sitting in their own "folder" until restored back to active.
const FolderSection = ({ title, tone, creators, actionLabel, onAction, onRemove, testId }) => {
  const [open, setOpen] = useState(false);
  if (!creators.length) return null;
  const toneCls = tone === 'declined' ? 'border-red-200 bg-red-50/50 text-red-700' : 'border-lilac-300 bg-lilac-50/50 text-lilac-700';
  const btnCls = tone === 'declined' ? 'bg-white border border-red-200 text-red-600 hover:bg-red-50' : 'bg-white border border-lilac-300 text-lilac-700 hover:bg-lilac-50';
  return (
    <div className={`border rounded-2xl mb-3 ${toneCls}`} data-testid={testId}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 px-4 py-3 text-left" data-testid={`${testId}-toggle`}>
        <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        <span className="text-[11px] uppercase tracking-widest font-semibold">{title} ({creators.length})</span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-1.5">
          {creators.map((c) => (
            <div key={c.id} className="flex items-center gap-2 bg-white rounded-xl px-3 py-2" data-testid={`${testId}-row-${c.id}`}>
              <span className="shrink-0 w-7 h-7 rounded-full overflow-hidden bg-pink-50 border border-pink-100 flex items-center justify-center">
                {c.photo_url ? <img src={c.photo_url} alt="" className="w-full h-full object-cover" /> : <User className="w-3.5 h-3.5 text-pink-300" />}
              </span>
              <span className="flex-1 text-sm text-slate-700 truncate">{c.name || 'unnamed'}</span>
              <button onClick={() => onAction(c.id)} className={`text-xs px-2.5 h-7 rounded-full font-medium whitespace-nowrap ${btnCls}`} data-testid={`${testId}-action-${c.id}`}>
                {actionLabel}
              </button>
              <button onClick={() => onRemove(c.id)} className="text-slate-300 hover:text-red-600 p-1 shrink-0" data-testid={`${testId}-remove-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// 3-state filter pill: any → yes → no → any
const FilterPill = ({ label, state, onCycle, testId }) => {
  const meta = {
    any: { text: `${label}: any`, cls: 'bg-white text-slate-500 border-slate-200' },
    yes: { text: `${label}: yes`, cls: 'bg-mint-50 text-mint-700 border-mint-300' },
    no: { text: `${label}: no`, cls: 'bg-pink-50 text-pink-700 border-pink-300' },
  }[state];
  return (
    <button type="button" onClick={onCycle} className={`px-2.5 h-7 rounded-full text-[11px] font-medium border whitespace-nowrap ${meta.cls}`} data-testid={testId}>
      {meta.text}
    </button>
  );
};

// Repeatable link rows — press "+" to add another post link, each with its own notes/likes/views(/gmv)
const emptyLinkRow = () => ({ url: '', likes: null, views: null, gmv: null, notes: '' });

const ContentLinksField = ({ label, links, showGmv, onCommit, testId }) => {
  const [local, setLocal] = useState(links && links.length ? links : [emptyLinkRow()]);
  useEffect(() => { setLocal(links && links.length ? links : [emptyLinkRow()]); }, [links]);

  const update = (next) => { setLocal(next.length ? next : [emptyLinkRow()]); onCommit(next); };
  const patchAt = (i, patch) => update(local.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const setUrlAt = (i, url) => setLocal((l) => l.map((r, idx) => (idx === i ? { ...r, url } : r)));
  const setNotesAt = (i, notes) => setLocal((l) => l.map((r, idx) => (idx === i ? { ...r, notes } : r)));
  const commitText = () => onCommit(local);
  const addRow = () => update([...local, emptyLinkRow()]);
  const removeRow = (i) => update(local.filter((_, idx) => idx !== i));

  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-400 font-medium">{label}</label>
      <div className="mt-1.5 space-y-1.5">
        {local.map((row, i) => (
          <div key={i} className="flex items-center gap-1.5 flex-wrap">
            <Input
              value={row.notes || ''}
              onChange={(e) => setNotesAt(i, e.target.value)}
              onBlur={commitText}
              placeholder="notes"
              className="h-8 text-xs w-28"
              data-testid={`${testId}-notes-${i}`}
            />
            <Input
              value={row.url || ''}
              onChange={(e) => setUrlAt(i, e.target.value)}
              onBlur={commitText}
              placeholder="link to post"
              className="h-8 text-xs flex-1 min-w-[150px]"
              data-testid={`${testId}-url-${i}`}
            />
            <NumberInput value={row.likes ?? ''} onChange={(v) => patchAt(i, { likes: v })} placeholder="likes" className="h-8 text-xs w-20" data-testid={`${testId}-likes-${i}`} />
            <NumberInput value={row.views ?? ''} onChange={(v) => patchAt(i, { views: v })} placeholder="views" className="h-8 text-xs w-20" data-testid={`${testId}-views-${i}`} />
            {showGmv && (
              <NumberInput value={row.gmv ?? ''} onChange={(v) => patchAt(i, { gmv: v })} placeholder="gmv £" className="h-8 text-xs w-20" data-testid={`${testId}-gmv-${i}`} />
            )}
            <button type="button" onClick={() => removeRow(i)} className="text-slate-300 hover:text-red-600 p-1" data-testid={`${testId}-remove-${i}`}><X className="w-3 h-3" /></button>
          </div>
        ))}
        <button type="button" onClick={addRow} className="inline-flex items-center gap-1 text-[11px] text-pink-600 hover:text-pink-700" data-testid={`${testId}-add`}>
          <Plus className="w-3 h-3" /> add link
        </button>
      </div>
    </div>
  );
};

// One deliverable's own tracking — name, due/live dates, brief link, workflow toggles
const emptyDeliverable = () => ({ id: 'dlv_' + Math.random().toString(36).slice(2, 10), name: '', due_date: '', live_date: '', brief_link: '', contracted: false, briefed: false, live: false, paid: false, boosting_code_ig: '', boosting_code_tt: '' });

const DeliverablesField = ({ items, onCommit, creatorId }) => {
  const [openIdx, setOpenIdx] = useState(null);
  const [local, setLocal] = useState(items || []);
  useEffect(() => { setLocal(items || []); }, [items]);
  const rows = local;
  if (!rows.length) return null;

  // typed text fields buffer locally and only PATCH on blur — committing on every keystroke
  // caused the server's echoed (stale) response to race ahead of fast typing and drop letters
  const setLocalAt = (i, patch) => setLocal((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const patchAt = (i, patch) => { const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)); setLocal(next); onCommit(next); };
  const commitNow = () => onCommit(rows);

  return (
    <div className="mt-3 pt-3 border-t border-pink-100 space-y-2">
      <label className="text-[10px] uppercase tracking-widest text-slate-400 font-medium">deliverables ({rows.length})</label>
      {rows.map((d, i) => {
        const open = openIdx === i;
        return (
          <div key={d.id || i} className="border border-pink-100 rounded-lg overflow-hidden" data-testid={`mailer-deliverable-${creatorId}-${i}`}>
            <div className="w-full flex items-center gap-2 px-3 py-2 bg-pink-50/60">
              <button type="button" onClick={() => setOpenIdx(open ? null : i)} data-testid={`mailer-deliverable-toggle-${creatorId}-${i}`}>
                <ChevronDown className={`w-3.5 h-3.5 text-pink-400 transition-transform ${open ? 'rotate-180' : ''}`} />
              </button>
              <Input
                value={d.name || ''}
                onChange={(e) => setLocalAt(i, { name: e.target.value })}
                onBlur={commitNow}
                placeholder={`deliverable ${i + 1} name`}
                className="h-7 text-xs font-medium bg-white flex-1"
                data-testid={`mailer-deliverable-name-${creatorId}-${i}`}
              />
            </div>
            {open && (
              <div className="p-3 grid grid-cols-2 md:grid-cols-4 gap-2 bg-white">
                <Field label="due date">
                  <Input type="date" value={d.due_date || ''} onChange={(e) => patchAt(i, { due_date: e.target.value })} className="h-7 text-xs" data-testid={`mailer-deliverable-due-${creatorId}-${i}`} />
                </Field>
                <Field label="live date">
                  <Input type="date" value={d.live_date || ''} onChange={(e) => patchAt(i, { live_date: e.target.value })} className="h-7 text-xs" data-testid={`mailer-deliverable-live-${creatorId}-${i}`} />
                </Field>
                <div className="col-span-2">
                  <Field label="link to brief">
                    <Input value={d.brief_link || ''} onChange={(e) => setLocalAt(i, { brief_link: e.target.value })} onBlur={commitNow} placeholder="https://…" className="h-7 text-xs" data-testid={`mailer-deliverable-brief-${creatorId}-${i}`} />
                  </Field>
                </div>
                <div className="col-span-2 md:col-span-4 flex items-center gap-2 flex-wrap pt-1">
                  <ToggleChip label="briefed" checked={!!d.briefed} onChange={() => patchAt(i, { briefed: !d.briefed })} testId={`mailer-deliverable-briefed-${creatorId}-${i}`} />
                  <ToggleChip label="live" checked={!!d.live} onChange={() => patchAt(i, { live: !d.live })} testId={`mailer-deliverable-live-toggle-${creatorId}-${i}`} />
                  <ToggleChip label="paid" checked={!!d.paid} onChange={() => patchAt(i, { paid: !d.paid })} testId={`mailer-deliverable-paid-${creatorId}-${i}`} />
                </div>
                <div className="w-32">
                  <Field label="ig boosting code">
                    <Input
                      value={d.boosting_code_ig || ''}
                      onChange={(e) => setLocalAt(i, { boosting_code_ig: e.target.value })}
                      onBlur={commitNow}
                      placeholder="optional"
                      className="h-7 text-xs bg-white"
                      data-testid={`mailer-deliverable-boost-ig-${creatorId}-${i}`}
                    />
                  </Field>
                </div>
                <div className="w-32">
                  <Field label="tt boosting code">
                    <Input
                      value={d.boosting_code_tt || ''}
                      onChange={(e) => setLocalAt(i, { boosting_code_tt: e.target.value })}
                      onBlur={commitNow}
                      placeholder="optional"
                      className="h-7 text-xs bg-white"
                      data-testid={`mailer-deliverable-boost-tt-${creatorId}-${i}`}
                    />
                  </Field>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const MailerCreatorCard = ({ creator, isAffiliate, isPaid, onPatch, onFetched, onRemove, campaignId, clientId, excludeContactIds, products = [] }) => {
  const [local, setLocal] = useState(creator);
  const [uploading, setUploading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  useEffect(() => { setLocal(creator); }, [creator]);

  const setField = (patch) => setLocal((l) => ({ ...l, ...patch }));
  const commit = (patch) => onPatch(patch);
  const toggle = (key) => { const v = !local[key]; setField({ [key]: v }); commit({ [key]: v }); };
  const setStatus = (status) => { setField({ status }); commit({ status }); };

  // Empty card (no name/link yet) can be filled in-place from an existing database
  // contact — same picker the sheet view's blank rows use.
  const isEmptyCard = !local.name && !local.ig_link && !local.tt_link;
  const fillFromContact = (contact) => {
    const patch = {
      name: contact.name || '', ig_link: contact.ig_link || '', tt_link: contact.tt_link || '',
      ig_followers: contact.ig_followers, tt_followers: contact.tt_followers,
      email: contact.email || '', address: contact.address || '', notes: contact.notes || '',
      photo_url: contact.photo_url || '', tier: contact.tier, custom_tag: contact.custom_tag || '',
    };
    setField(patch);
    commit(patch);
    toast.success(`filled in ${contact.name || 'creator'}`);
    setPickerOpen(false);
  };

  const [fetchingIg, setFetchingIg] = useState(false);
  const [fetchingTt, setFetchingTt] = useState(false);
  // Always saves the link first (even if blank/unrecognised); only fetches when it looks
  // like a real url — waiting for the save to land first avoids a race where its response
  // arrives after the fetch's and stomps the freshly-pulled follower count back to blank.
  const commitLinkAndFetch = async (platform, url) => {
    const field = platform === 'ig' ? 'ig_link' : 'tt_link';
    await commit({ [field]: url });
    if (!url || !/^https?:\/\//i.test(url)) return;
    const setBusy = platform === 'ig' ? setFetchingIg : setFetchingTt;
    setBusy(true);
    try {
      const { data } = await api.post(`/mailers/creators/${creator.id}/fetch-profile`, { platform, url });
      if (data.ok && data.creator) {
        setLocal(data.creator);
        onFetched?.(data.creator);
        if (platform === 'ig' && data.estimated) {
          toast.info('instagram blocked direct access — estimated follower count via web search');
        } else {
          toast.success(platform === 'ig' ? 'pulled instagram follower count' : 'pulled tiktok follower count + photo');
        }
      } else {
        toast.info(data.reason || 'could not auto-fetch — add manually');
      }
    } catch { toast.info('could not auto-fetch — add manually'); }
    finally { setBusy(false); }
  };

  const handlePhoto = async (file) => {
    setPhotoUploading(true);
    try {
      const url = await uploadImage(file);
      setField({ photo_url: url });
      commit({ photo_url: url });
    } catch { toast.error('photo upload failed'); }
    finally { setPhotoUploading(false); }
  };

  const setDeliverablesCount = (countRaw) => {
    const count = Math.max(0, Math.min(50, Number(countRaw) || 0));
    let items = [...(local.deliverable_items || [])];
    if (items.length < count) { while (items.length < count) items.push(emptyDeliverable()); }
    else if (items.length > count) { items = items.slice(0, count); }
    setField({ deliverables_count: count, deliverable_items: items });
    commit({ deliverables_count: count, deliverable_items: items });
  };
  const commitDeliverables = (items) => { setField({ deliverable_items: items }); commit({ deliverable_items: items }); };

  const handleScreenshots = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) { const u = await uploadImage(f); urls.push(u); }
      const next = [...(local.screenshots || []), ...urls];
      setField({ screenshots: next });
      commit({ screenshots: next });
    } catch { toast.error('upload failed'); }
    finally { setUploading(false); }
  };
  const removeScreenshot = (url) => {
    const next = (local.screenshots || []).filter((s) => s !== url);
    setField({ screenshots: next });
    commit({ screenshots: next });
  };  return (
    <div className="bg-gradient-to-br from-pink-50/60 to-white border border-pink-200 rounded-xl p-3" data-testid={`mailer-creator-${creator.id}`}>
      <div className="flex items-start gap-2.5 flex-wrap">
        <label className="shrink-0 w-9 h-9 rounded-full overflow-hidden border-2 border-pink-200 flex items-center justify-center cursor-pointer bg-pink-50 hover:border-pink-400 relative group" data-testid={`mailer-photo-${creator.id}`}>
          {local.photo_url ? (
            <img src={local.photo_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <User className="w-4 h-4 text-pink-300" />
          )}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity">
            {photoUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3 h-3" />}
          </div>
          <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handlePhoto(f); }} data-testid={`mailer-photo-input-${creator.id}`} />
        </label>
        <div className="flex-1 min-w-[160px]">
          <Field label="name">
            <div className="flex items-center gap-1.5">
              {isEmptyCard && (
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className="shrink-0 h-8 w-8 flex items-center justify-center rounded-md border border-pink-200 text-slate-400 hover:text-pink-600 hover:border-pink-400"
                  title="add from database"
                  data-testid={`mailer-creator-adddb-${creator.id}`}
                >
                  <Database className="w-3.5 h-3.5" />
                </button>
              )}
              <Input value={local.name || ''} onChange={(e) => setField({ name: e.target.value })} onBlur={(e) => commit({ name: e.target.value })} placeholder="creator name" className="h-7 text-sm font-semibold" data-testid={`mailer-creator-name-${creator.id}`} />
              <TierBadge tier={local.tier} onCycle={(t) => { setField({ tier: t }); commit({ tier: t }); }} testId={`mailer-tier-badge-${creator.id}`} />
            </div>
          </Field>
          {pickerOpen && <SheetContactPickerDialog onPick={fillFromContact} onClose={() => setPickerOpen(false)} campaignId={campaignId} clientId={clientId} excludeContactIds={excludeContactIds} />}
        </div>
        <div className="w-32">
          <Field label="custom tag">
            <Input
              value={local.custom_tag || ''}
              onChange={(e) => setField({ custom_tag: e.target.value })}
              onBlur={(e) => commit({ custom_tag: e.target.value })}
              placeholder="e.g. VIP"
              className="h-7 text-xs"
              data-testid={`mailer-custom-tag-${creator.id}`}
            />
          </Field>
        </div>
        {products.length > 0 && (
          <div className="w-40">
            <Field label="product">
              <Select
                value={local.product ? local.product : '__none__'}
                onValueChange={(v) => { const next = v === '__none__' ? '' : v; setField({ product: next }); commit({ product: next }); }}
              >
                <SelectTrigger className="h-8 text-xs bg-white" data-testid={`mailer-product-${creator.id}`}><SelectValue placeholder="select product" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— none —</SelectItem>
                  {products.map((p, i) => <SelectItem key={`${p}-${i}`} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
        )}
        <div className="min-w-[160px]">
          <Field label="email">
            <Input
              type="email"
              value={local.email || ''}
              onChange={(e) => setField({ email: e.target.value })}
              onBlur={(e) => commit({ email: e.target.value })}
              placeholder="creator@email.com"
              className="h-7 text-xs"
              data-testid={`mailer-email-${creator.id}`}
            />
          </Field>
        </div>
        {creator.contact_id && (
          <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10px] font-medium bg-mint-50 text-mint-700 border border-mint-200 self-center" title="added to database">
            <CheckCircle2 className="w-3 h-3" /> in database
          </span>
        )}
        <button onClick={onRemove} className="ml-auto text-slate-300 hover:text-red-600 self-start p-1" data-testid={`mailer-remove-creator-${creator.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>

      <div className="flex items-start gap-2.5 flex-wrap mt-2">
        {!isAffiliate && (
          <div className="min-w-[195px]">
            <Field label="instagram link">
              <div className="flex items-center gap-1">
                <Input value={local.ig_link || ''} onChange={(e) => setField({ ig_link: e.target.value })} onBlur={(e) => commitLinkAndFetch('ig', e.target.value)} placeholder="https://instagram.com/…" className="h-7 text-xs" data-testid={`mailer-ig-link-${creator.id}`} />
                {fetchingIg && <Loader2 className="w-3.5 h-3.5 text-pink-400 animate-spin shrink-0" />}
                <LinkActions url={local.ig_link} testId={`mailer-ig-link-${creator.id}`} />
              </div>
            </Field>
          </div>
        )}
        {!isAffiliate && (
          <div className="w-28">
            <Field label="ig followers">
              <NumberInput value={local.ig_followers ?? ''} onChange={(v) => setField({ ig_followers: v })} onBlur={() => commit({ ig_followers: local.ig_followers })} className="h-7 text-xs" />
            </Field>
          </div>
        )}
        <div className="min-w-[195px]">
          <Field label="tiktok link">
            <div className="flex items-center gap-1">
              <Input value={local.tt_link || ''} onChange={(e) => setField({ tt_link: e.target.value })} onBlur={(e) => commitLinkAndFetch('tt', e.target.value)} placeholder="https://tiktok.com/@…" className="h-7 text-xs" data-testid={`mailer-tt-link-${creator.id}`} />
              {fetchingTt && <Loader2 className="w-3.5 h-3.5 text-pink-400 animate-spin shrink-0" />}
              <LinkActions url={local.tt_link} testId={`mailer-tt-link-${creator.id}`} />
            </div>
          </Field>
        </div>
        <div className="w-28">
          <Field label="tt followers">
            <NumberInput value={local.tt_followers ?? ''} onChange={(v) => setField({ tt_followers: v })} onBlur={() => commit({ tt_followers: local.tt_followers })} className="h-7 text-xs" />
          </Field>
        </div>
        {isAffiliate && (
          <div className="w-20">
            <Field label="level (1-6)">
              <NumberInput value={local.level ?? ''} onChange={(v) => setField({ level: v })} onBlur={() => commit({ level: local.level })} className="h-7 text-xs" data-testid={`mailer-level-${creator.id}`} />
            </Field>
          </div>
        )}
        {isAffiliate && (
          <div className="w-24">
            <Field label="post rate (%)">
              <NumberInput value={local.post_rate ?? ''} onChange={(v) => setField({ post_rate: v })} onBlur={() => commit({ post_rate: local.post_rate })} className="h-7 text-xs" data-testid={`mailer-post-rate-${creator.id}`} />
            </Field>
          </div>
        )}
        {isAffiliate && (
          <div className="w-32">
            <Field label="gmv last 30d (£)">
              <NumberInput value={local.gmv_last_30_days ?? ''} onChange={(v) => setField({ gmv_last_30_days: v })} onBlur={() => commit({ gmv_last_30_days: local.gmv_last_30_days })} className="h-7 text-xs" data-testid={`mailer-gmv30-${creator.id}`} />
            </Field>
          </div>
        )}
        {!isAffiliate && (
          <div className="w-32">
            <Field label="affiliate code">
              <Input value={local.affiliate_code || ''} onChange={(e) => setField({ affiliate_code: e.target.value })} onBlur={(e) => commit({ affiliate_code: e.target.value })} placeholder="optional" className="h-7 text-xs" data-testid={`mailer-affiliate-code-${creator.id}`} />
            </Field>
          </div>
        )}
        {isPaid && (
          <div className="w-24">
            <Field label="rate (£)">
              <NumberInput value={local.rate ?? ''} onChange={(v) => setField({ rate: v })} onBlur={() => commit({ rate: local.rate })} className="h-7 text-xs" data-testid={`mailer-rate-${creator.id}`} />
            </Field>
          </div>
        )}
        {isPaid && (
          <div className="min-w-[160px]">
            <Field label="deliverables">
              <Input value={local.deliverables || ''} onChange={(e) => setField({ deliverables: e.target.value })} onBlur={(e) => commit({ deliverables: e.target.value })} placeholder="e.g. 2 reels + 3 stories" className="h-7 text-xs" data-testid={`mailer-deliverables-${creator.id}`} />
            </Field>
          </div>
        )}
        {isPaid && (
          <div className="w-28">
            <Field label="number of deliverables">
              <NumberInput value={local.deliverables_count ?? ''} onChange={setDeliverablesCount} className="h-7 text-xs" data-testid={`mailer-deliverables-count-${creator.id}`} />
            </Field>
          </div>
        )}
        {isPaid && (
          <div className="min-w-[140px]">
            <Field label="usage">
              <Input value={local.usage || ''} onChange={(e) => setField({ usage: e.target.value })} onBlur={(e) => commit({ usage: e.target.value })} placeholder="e.g. 3mo paid usage" className="h-7 text-xs" data-testid={`mailer-usage-${creator.id}`} />
            </Field>
          </div>
        )}
      </div>

      {isPaid && <DeliverablesField items={local.deliverable_items} onCommit={commitDeliverables} creatorId={creator.id} />}

      <div className="flex items-center gap-x-3 gap-y-1.5 flex-wrap mt-2.5 pt-2.5 border-t border-pink-100">
        <ToggleChip label="contacted" checked={!!local.contacted} onChange={() => toggle('contacted')} testId={`toggle-contacted-${creator.id}`} />
        <ToggleChip label="address received" checked={!!local.address_received} onChange={() => toggle('address_received')} testId={`toggle-address-${creator.id}`} />
        <ToggleChip label="product sent" checked={!!local.product_sent} onChange={() => toggle('product_sent')} testId={`toggle-sent-${creator.id}`} />
        <ToggleChip label="product received" checked={!!local.product_received} onChange={() => toggle('product_received')} testId={`toggle-received-${creator.id}`} />
        <ToggleChip label="content posted" checked={!!local.content_posted} onChange={() => toggle('content_posted')} testId={`toggle-posted-${creator.id}`} />
        <div className="w-px h-4 bg-pink-100" />
        <ToggleChip label="declined" checked={local.status === 'declined'} onChange={(v) => setStatus(v ? 'declined' : 'active')} testId={`toggle-declined-${creator.id}`} />
        <ToggleChip label="backup" checked={local.status === 'backup'} onChange={(v) => setStatus(v ? 'backup' : 'active')} testId={`toggle-backup-${creator.id}`} />
      </div>

      <div className="mt-2.5 pt-2.5 border-t border-pink-100 grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="notes">
          <Textarea value={local.notes || ''} onChange={(e) => setField({ notes: e.target.value })} onBlur={(e) => commit({ notes: e.target.value })} rows={2} className="text-xs" data-testid={`mailer-notes-${creator.id}`} />
        </Field>
        <Field label="address">
          <Textarea value={local.address || ''} onChange={(e) => setField({ address: e.target.value })} onBlur={(e) => commit({ address: e.target.value })} rows={2} className="text-xs" data-testid={`mailer-address-${creator.id}`} />
        </Field>
        {isPaid && (
          <Field label="boosting code">
            <Input value={local.boosting_code || ''} onChange={(e) => setField({ boosting_code: e.target.value })} onBlur={(e) => commit({ boosting_code: e.target.value })} placeholder="optional" className="h-7 text-xs" data-testid={`mailer-boosting-code-${creator.id}`} />
          </Field>
        )}
      </div>

      {local.content_posted && (
        <div className="mt-2.5 flex flex-col gap-2.5 pt-2.5 border-t border-pink-100">
          {!isAffiliate && (
            <ContentLinksField
              label="ig content links"
              links={local.ig_content_links}
              showGmv={false}
              onCommit={(links) => { setField({ ig_content_links: links }); commit({ ig_content_links: links }); }}
              testId={`mailer-ig-content-${creator.id}`}
            />
          )}
          <ContentLinksField
            label="tiktok content links"
            links={local.tt_content_links}
            showGmv={isAffiliate}
            onCommit={(links) => { setField({ tt_content_links: links }); commit({ tt_content_links: links }); }}
            testId={`mailer-tt-content-${creator.id}`}
          />
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400 font-medium">screenshots (story mentions)</label>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              {(local.screenshots || []).map((url) => (
                <div key={url} className="relative w-11 h-11 rounded-md overflow-hidden ring-1 ring-slate-200 group">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removeScreenshot(url)} className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <label className="w-11 h-11 rounded-md border border-dashed border-pink-300 flex items-center justify-center cursor-pointer text-slate-400 hover:text-pink-600 hover:border-pink-400">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { const files = Array.from(e.target.files || []); e.target.value = ''; handleScreenshots(files); }} data-testid={`mailer-screenshot-upload-${creator.id}`} />
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ImportSpreadsheetButton = ({ campaignId, onImported }) => {
  const [busy, setBusy] = useState(false);
  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/mailers/campaigns/${campaignId}/import-spreadsheet`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onImported?.(data.creators || []);
      toast.success(`imported — ${data.added} added, ${data.merged} merged`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'import failed'); }
    finally { setBusy(false); }
  };
  return (
    <label className={`inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-[11px] font-medium border transition-colors cursor-pointer ${busy ? 'opacity-60 pointer-events-none' : 'text-slate-600 bg-white border-pink-200 hover:border-pink-400 hover:text-pink-700'}`} data-testid="mailer-import-spreadsheet-btn" title="upload an Excel/CSV — Claude reads the contacts, fills them in, and merges duplicates">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} import spreadsheet
      <input type="file" accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handleFile(f); }} data-testid="mailer-import-spreadsheet-input" />
    </label>
  );
};

const AddFromDatabaseButton = ({ campaignId, clientId, excludeContactIds, onAdded }) => {
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState([]);
  const [adding, setAdding] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);

  const openModal = async () => {
    setOpen(true);
    if (!loaded) {
      try {
        const { data } = await api.get('/database/contacts');
        setContacts(data || []);
        setLoaded(true);
      } catch { toast.error('failed to load contacts'); }
    }
  };

  const closeModal = () => { setOpen(false); setQ(''); setSelected([]); setActiveFilters([]); };

  const duplicateIds = useMemo(() => computeDuplicateIds(contacts), [contacts]);
  const toggleFilter = (key) => setActiveFilters((f) => (key === null ? [] : f.includes(key) ? f.filter((k) => k !== key) : [...f, key]));

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    const arr = contacts.filter((c) => {
      if (excludeContactIds?.has(c.id)) return false;
      if (t && ![c.name, c.handle, c.company].filter(Boolean).some((v) => v.toLowerCase().includes(t))) return false;
      if (activeFilters.length && !activeFilters.every((key) => pickerMatchesFilter(c, key, clientId, campaignId))) return false;
      return true;
    });
    return sortContactsByName(arr);
  }, [contacts, q, activeFilters, clientId, campaignId, excludeContactIds]);

  const toggleSel = (cid) => setSelected((s) => (s.includes(cid) ? s.filter((x) => x !== cid) : [...s, cid]));

  const confirmAdd = async () => {
    if (!selected.length) return;
    setAdding(true);
    try {
      const { data } = await api.post(`/mailers/campaigns/${campaignId}/creators/from-contacts`, { contact_ids: selected });
      onAdded(data);
      toast.success(`${selected.length} creator${selected.length === 1 ? '' : 's'} added from database`);
      closeModal();
    } catch (e) { toast.error(e?.response?.data?.detail || 'failed to add'); }
    finally { setAdding(false); }
  };

  return (
    <>
      <Button onClick={openModal} variant="outline" className="h-7 text-xs" data-testid="mailer-add-from-database-btn">
        <Search className="w-3.5 h-3.5 mr-1" /> add from database
      </Button>
      <Dialog open={open} onOpenChange={(v) => { if (!v) closeModal(); else setOpen(true); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>add creators from database</DialogTitle><DialogDescription className="sr-only">select existing contacts to add to this mailer</DialogDescription></DialogHeader>
          <PickerCountLine count={contacts.length} testId="mailer-database-count" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="search name, handle, company…" className="h-9 text-xs" data-testid="mailer-database-search" />
          <PickerFilterBar activeFilters={activeFilters} onToggle={toggleFilter} />
          <div className="max-h-72 overflow-y-auto space-y-1 mt-2">
            {filtered.length === 0 ? (
              <div className="text-center text-xs text-slate-400 py-6">no contacts found</div>
            ) : filtered.map((c) => (
              <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-pink-50 cursor-pointer" data-testid={`mailer-database-contact-${c.id}`}>
                <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggleSel(c.id)} className="accent-pink-500" />
                {c.photo_url ? (
                  <img src={c.photo_url} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
                ) : (
                  <span className="w-6 h-6 rounded-full bg-pink-50 flex items-center justify-center shrink-0"><User className="w-3 h-3 text-pink-300" /></span>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-slate-700 truncate">{c.name}</div>
                  {c.handle && <div className="text-[11px] text-slate-400 truncate">{c.handle}</div>}
                </div>
                {duplicateIds.has(c.id) && <DuplicateTag />}
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeModal} className="h-7 text-xs">cancel</Button>
            <Button onClick={confirmAdd} disabled={!selected.length || adding} className="h-8 text-xs bg-pink-500 hover:bg-pink-600 text-white" data-testid="mailer-database-confirm-add">
              {adding ? 'adding…' : `add ${selected.length || ''} selected`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ------- Kanban grid view — small draggable tiles, one column per stage -------
const MailerKanbanTile = ({ creator, onClick, dragProps }) => (
  <div
    className="bg-white border border-pink-100 rounded-xl p-2 flex items-center gap-2 cursor-pointer hover:border-pink-300 hover:shadow-sm transition-all"
    onClick={onClick}
    data-testid={`mailer-kanban-tile-${creator.id}`}
    {...dragProps}
  >
    <span className="shrink-0 w-8 h-8 rounded-full overflow-hidden bg-pink-50 border border-pink-100 flex items-center justify-center">
      {creator.photo_url ? <img src={creator.photo_url} alt="" className="w-full h-full object-cover" /> : <User className="w-3.5 h-3.5 text-pink-300" />}
    </span>
    <span className="flex-1 min-w-0 text-xs font-medium text-slate-700 truncate">{creator.name || 'unnamed'}</span>
    <div className="flex items-center gap-0.5 shrink-0">
      {creator.ig_link && <span className="text-[8px] font-bold text-slate-400 bg-slate-100 rounded px-1">IG</span>}
      {creator.tt_link && <span className="text-[8px] font-bold text-slate-400 bg-slate-100 rounded px-1">TT</span>}
    </div>
  </div>
);

const MailerKanbanColumn = ({ col, creators, onDrop, dragging, setDragging, onCardClick }) => {
  const [over, setOver] = useState(false);
  return (
    <div
      className={`flex-1 min-w-[190px] flex flex-col rounded-xl border overflow-hidden transition-all bg-white ${over ? 'border-pink-400 shadow-lg shadow-pink-100' : 'border-slate-200'}`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onDrop(col.key); }}
      data-testid={`mailer-kanban-column-${col.key}`}
    >
      <div className="h-1.5 w-full" style={{ background: 'linear-gradient(90deg,#d394ab 0%,#e9c2d5 60%,#d3c1e1 100%)' }} />
      <div className="px-3 py-3 flex items-center justify-between bg-gradient-to-b from-pink-50/60 to-white">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: col.color }} />
          <h3 className="text-sm font-bold lowercase whitespace-nowrap" style={{ color: '#8a2c5b' }}>{col.label}</h3>
        </div>
        <span className="text-xs font-semibold inline-flex items-center justify-center min-w-[20px] h-5 rounded-full px-1.5 shrink-0" style={{ background: '#d394ab', color: 'white' }}>{creators.length}</span>
      </div>
      <div className="flex-1 p-2 space-y-1.5 overflow-y-auto bg-slate-50/40" style={{ minHeight: 220 }}>
        {creators.map((c) => (
          <MailerKanbanTile
            key={c.id}
            creator={c}
            onClick={() => onCardClick(c)}
            dragProps={{
              draggable: true,
              onDragStart: () => setDragging(c.id),
              onDragEnd: () => setDragging(null),
              style: { opacity: dragging === c.id ? 0.5 : 1 },
            }}
          />
        ))}
        {creators.length === 0 && (
          <div className="text-center text-[11px] text-slate-400 py-6 border border-dashed border-slate-200 rounded-lg">drop here</div>
        )}
      </div>
    </div>
  );
};

// Base identity/contact columns — content link/views/likes groups are appended dynamically
// per creator (see getContentSlot) since a creator can post more than one piece of content.
const SHEET_COL_WIDTH = 150;
const SHEET_BASE_COLUMNS = [
  { key: 'name', label: 'name', width: SHEET_COL_WIDTH },
  { key: 'ig_link', label: 'instagram', width: SHEET_COL_WIDTH },
  { key: 'ig_followers', label: 'ig following', width: SHEET_COL_WIDTH / 2 + 20 },
  { key: 'tt_link', label: 'tiktok', width: SHEET_COL_WIDTH },
  { key: 'tt_followers', label: 'tt following', width: SHEET_COL_WIDTH / 2 + 20 },
  { key: 'email', label: 'email', width: SHEET_COL_WIDTH },
  { key: 'contacted', label: 'contacted', width: 85 },
  { key: 'follow_up', label: 'follow up?!', width: 90 },
  { key: 'product_sent', label: 'address sent', width: 95 },
  { key: 'content_posted', label: 'posted?', width: 80 },
  { key: 'feedback', label: 'follow up / feedback?!', width: 200 },
];;

// Every content item a creator has posted, in order — ig links first, then tt links — each
// tagged with where it lives so edits write back to the right array/index.
const getContentEntries = (c) => {
  const ig = (c.ig_content_links || []).map((entry, idx) => ({ arrKey: 'ig_content_links', idx, entry }));
  const tt = (c.tt_content_links || []).map((entry, idx) => ({ arrKey: 'tt_content_links', idx, entry }));
  return [...ig, ...tt];
};
// The Nth content slot for a creator — an existing entry if they have one, otherwise a blank
// shell that (once typed into) will be appended to the campaign-type-default array.
const getContentSlot = (c, colIdx, isAffiliate) => {
  const entries = getContentEntries(c);
  if (entries[colIdx]) return entries[colIdx];
  const arrKey = isAffiliate ? 'tt_content_links' : 'ig_content_links';
  return { arrKey, idx: (c[arrKey] || []).length, entry: { url: '', likes: '', views: '' } };
};

const SHEET_TINTS = {
  slate:  'bg-slate-100 text-slate-800 border-slate-200',
  white:  'bg-white text-slate-700 border-slate-200',
  rose:   'bg-rose-50 text-rose-700 border-rose-200',
  violet: 'bg-violet-50 text-violet-700 border-violet-200',
  amber:  'bg-amber-50 text-amber-700 border-amber-200',
};
// Pill-bubble cell — each value sits in a soft coloured pill (deals-list style) but is still
// a spreadsheet-style input you type straight into. `showOpenLink` adds an open-in-new-tab icon.
const SheetCell = ({ value, onCommit, type = 'text', align = 'center', testId, showOpenLink = false, showCopy = false, tint = 'slate', bold = false, placeholder = '' }) => {
  const [v, setV] = useState(value ?? '');
  useEffect(() => { setV(value ?? ''); }, [value]);
  const handleFocus = async () => {
    const text = String(value ?? '').trim();
    if (!text) return;
    const ok = await copyToClipboard(text);
    if (ok) toast.success('cell copied', { duration: 1000 });
  };
  const isValidUrl = showOpenLink && value && /^https?:\/\//i.test(value);
  const alignCls = align === 'right' ? 'text-right' : align === 'left' ? 'text-left' : 'text-center';
  return (
    <div className="w-full h-full flex items-center gap-1 px-1.5">
      <input
        type={type}
        value={v}
        onChange={(e) => setV(e.target.value)}
        onFocus={handleFocus}
        onBlur={() => { if (String(v) !== String(value ?? '')) onCommit(v); }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
        placeholder={placeholder}
        className={`w-full h-8 px-3 text-xs rounded-full border outline-none truncate focus:bg-white focus:ring-1 focus:ring-inset focus:ring-pink-400 transition-colors ${SHEET_TINTS[tint] || SHEET_TINTS.slate} ${bold ? 'font-semibold' : 'font-medium'} ${alignCls}`}
        data-testid={testId}
      />
      {isValidUrl && (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="shrink-0 text-slate-400 hover:text-pink-600"
          title="open in new tab"
          data-testid={`${testId}-open`}
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
      {showCopy && value && String(value).trim() && (
        <button
          type="button"
          onClick={async (e) => { e.stopPropagation(); const ok = await copyToClipboard(String(value).trim()); if (ok) toast.success('copied', { duration: 1000 }); }}
          className="shrink-0 text-slate-400 hover:text-pink-600"
          title="copy"
          data-testid={`${testId}-copy`}
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

// Pill toggle cell for the list view's yes/no columns — a soft coloured bubble that fills in
// when ticked, matching the deals-list pill aesthetic.
const SheetCheckCell = ({ checked, onToggle, testId, color = '#5fae8f', disabled = false }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onToggle}
    className={`w-full h-full flex items-center justify-center py-2.5 px-1.5 ${disabled ? 'cursor-default' : ''}`}
    data-testid={testId}
  >
    <span
      className={`inline-flex items-center justify-center h-8 w-14 rounded-full border transition ${checked ? 'text-white' : 'bg-slate-50 border-slate-200 text-slate-300'}`}
      style={checked ? { background: color, borderColor: color } : undefined}
    >
      <Check className="w-4 h-4" />
    </span>
  </button>
);

// "Follow up / feedback?!" cell — a tick plus a free-text box for words.
const SheetFeedbackCell = ({ checked, text, onToggle, onCommitText, testId }) => {
  const [v, setV] = useState(text ?? '');
  useEffect(() => { setV(text ?? ''); }, [text]);
  return (
    <div className="flex items-center gap-1 w-full h-full px-1">
      <button type="button" onClick={onToggle} className="shrink-0 flex items-center justify-center" data-testid={`${testId}-check`}>
        <span className={`w-5 h-5 rounded-md border flex items-center justify-center transition ${checked ? 'text-white' : 'bg-white border-slate-300 text-transparent'}`} style={checked ? { background: '#d394ab', borderColor: '#d394ab' } : undefined}>
          <Check className="w-3.5 h-3.5" />
        </span>
      </button>
      <input
        type="text"
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => { if (String(v) !== String(text ?? '')) onCommitText(v); }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
        placeholder="feedback…"
        className="flex-1 min-w-0 h-8 px-3 text-xs font-medium rounded-full border outline-none bg-amber-50 text-amber-700 border-amber-200 focus:bg-white focus:ring-1 focus:ring-inset focus:ring-pink-400 transition-colors"
        data-testid={`${testId}-text`}
      />
    </div>
  );
};

// Status cell — a colored pill button (matching the kanban column colors/dots) instead of a
// plain dropdown; opens the same 6-stage list to jump straight to a new stage.
const SheetStatusCell = ({ stage, onChange, testId }) => {
  const col = KANBAN_COLUMNS.find((k) => k.key === stage) || KANBAN_COLUMNS[0];
  return (
    <Select value={stage} onValueChange={onChange}>
      <SelectTrigger
        className="h-7 w-full border-0 rounded-full px-2.5 text-[11px] font-semibold text-white shadow-none focus:ring-1 focus:ring-inset focus:ring-white/60 [&>svg]:opacity-80 [&>svg]:w-3 [&>svg]:h-3"
        style={{ background: col.color }}
        data-testid={testId}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {KANBAN_COLUMNS.map((k) => (
          <SelectItem key={k.key} value={k.key}>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: k.color }} />
              {k.label}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

// Small sort-toggle arrow shared by the sheet column headers and the list view's sort
// toolbar — neutral (dim, up/down) when idle, solid up/down once it's the active sort.
const SortArrowBtn = ({ active, dir, onClick, testId, title }) => (
  <button type="button" onClick={onClick} title={title} className={`shrink-0 p-0.5 rounded transition-colors ${active ? 'text-pink-600' : 'text-slate-300 hover:text-slate-500'}`} data-testid={testId}>
    {active ? (dir === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3" />}
  </button>
);

// Google-Sheets-style editable grid, styled to match the kanban board's pink/lilac look —
// no add/remove controls per request, just cells (plus dynamic content-item column groups).
const MailerSheetView = ({ creators, isAffiliate, onPatch, onPatchContentLink, sortBy, sortDir, onToggleSort, campaignId, clientId, excludeContactIds, title }) => {
  const contentGroups = Math.max(1, ...creators.map((c) => getContentEntries(c).length));
  const [profileCreator, setProfileCreator] = useState(null);
  const [pickerForCreatorId, setPickerForCreatorId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);

  const addCoverage = (c) => {
    const arrKey = isAffiliate ? 'tt_content_links' : 'ig_content_links';
    const next = [...(c[arrKey] || []), { url: '', likes: '', views: '' }];
    onPatch(c.id, { [arrKey]: next });
  };
  const removeCoverage = (c, arrKey, idx) => {
    const next = (c[arrKey] || []).filter((_, i) => i !== idx);
    onPatch(c.id, { [arrKey]: next });
  };
  const uploadShots = async (c, files) => {
    if (!files?.length) return;
    setUploadingId(c.id);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadImage(f));
      onPatch(c.id, { screenshots: [...(c.screenshots || []), ...urls] });
    } catch { toast.error('upload failed'); }
    finally { setUploadingId(null); }
  };
  const removeShot = (c, url) => onPatch(c.id, { screenshots: (c.screenshots || []).filter((s) => s !== url) });

  const fillFromContact = (contact) => {
    onPatch(pickerForCreatorId, {
      name: contact.name || '', ig_link: contact.ig_link || '', tt_link: contact.tt_link || '',
      ig_followers: contact.ig_followers, tt_followers: contact.tt_followers,
      email: contact.email || '', address: contact.address || '', notes: contact.notes || '',
      photo_url: contact.photo_url || '', tier: contact.tier, custom_tag: contact.custom_tag || '',
    });
    toast.success(`filled in ${contact.name || 'creator'}`);
    setPickerForCreatorId(null);
  };

  // Shared row-building so export (.xlsx) and copy-to-clipboard (TSV) always match exactly.
  const buildRow = (c) => {
    const hasCoverage = getContentEntries(c).some((e) => (e.entry.url || '').trim());
    const yn = (b) => (b ? 'yes' : '');
    const row = {
      'Name': c.name || '',
      'Instagram': c.ig_link || '',
      'IG Following': c.ig_followers ?? '',
      'TikTok': c.tt_link || '',
      'TT Following': c.tt_followers ?? '',
      'Email': c.email || '',
      'Contacted': yn(c.contacted),
      'Follow Up?!': yn(c.follow_up),
      'Address Sent': yn(c.product_sent),
      'Posted?': yn(c.content_posted || hasCoverage),
      'Follow Up/Feedback?!': (c.feedback_needed ? 'yes ' : '') + (c.feedback || ''),
    };
    for (let i = 0; i < contentGroups; i++) {
      const { entry } = getContentSlot(c, i, isAffiliate);
      const suffix = i === 0 ? '' : ` ${i + 1}`;
      row[`Content Link${suffix}`] = entry.url || '';
      row[`Views${suffix}`] = entry.views ?? '';
      row[`Likes${suffix}`] = entry.likes ?? '';
    }
    return row;
  };
  const buildRows = () => creators.map(buildRow);

  const exportSheet = () => {
    const rows = buildRows();
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Creators');
    XLSX.writeFile(wb, 'mailer-sheet.xlsx');
    toast.success('downloaded — open or drag it into Google Sheets to import');
  };

  // Tab-separated, data only (no header row) so pasting straight into an existing Google
  // Sheet drops cells directly under your own headers without an extra row to delete.
  const copySheetData = async () => {
    const rows = buildRows();
    const headers = Object.keys(rows[0] || {});
    const tsv = rows.map((r) => headers.map((h) => r[h]).join('\t')).join('\n');
    try {
      await navigator.clipboard.writeText(tsv);
      toast.success('copied — paste straight into a Google Sheet');
    } catch {
      toast.error('copy failed — your browser may be blocking clipboard access');
    }
  };
  const copyRow = async (c) => {
    const row = buildRow(c);
    const tsv = Object.values(row).join('\t');
    try {
      await navigator.clipboard.writeText(tsv);
      toast.success(`copied ${c.name || 'row'}`);
    } catch {
      toast.error('copy failed — your browser may be blocking clipboard access');
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="mailer-sheet-view">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-pink-200" style={{ background: '#d394ab' }}>
        <div className="w-9 h-9 rounded-full bg-white/25 border border-white/40 flex items-center justify-center text-white font-semibold text-sm shrink-0 uppercase">
          {(title || 'M').trim().charAt(0) || 'M'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-white truncate">{title || 'creators'}</div>
          <div className="text-xs text-white/80">{creators.length} creator{creators.length === 1 ? '' : 's'}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={copySheetData}
            className="inline-flex items-center gap-1.5 px-3 h-7 rounded-full text-[11px] font-semibold text-white bg-white/20 border border-white/40 hover:bg-white/30 transition-colors"
            data-testid="mailer-sheet-copy-btn"
          >
            <Copy className="w-3 h-3" /> copy data
          </button>
          <button
            onClick={exportSheet}
            className="inline-flex items-center gap-1.5 px-3 h-7 rounded-full text-[11px] font-semibold bg-white text-[#8a2c5b] hover:bg-white/90 transition-colors"
            data-testid="mailer-sheet-export-btn"
          >
            <Download className="w-3 h-3" /> export (.xlsx)
          </button>
        </div>
      </div>
      <div className="overflow-auto max-h-[70vh]">
        <table
          className="text-xs"
          style={{ tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0, width: 36 + 360 + SHEET_BASE_COLUMNS.reduce((s, c) => s + c.width, 0) }}
          data-testid="mailer-sheet-table"
        >
          <thead className="bg-slate-50 sticky top-0 z-10" style={{ borderBottom: '1px solid #e2e8f0' }}>
            <tr className="text-[10px] uppercase tracking-widest text-slate-500">
              <th className="w-9 py-3"></th>
              {SHEET_BASE_COLUMNS.map((col) => (
                <th key={col.key} style={{ width: col.width }} className="text-center px-2 py-3 font-semibold whitespace-nowrap">
                  {['name', 'ig_followers', 'tt_followers'].includes(col.key) ? (
                    <span className="inline-flex items-center justify-center gap-1">
                      {col.label}
                      <SortArrowBtn
                        active={sortBy === col.key}
                        dir={sortDir}
                        onClick={() => onToggleSort(col.key)}
                        title={`sort by ${col.label}`}
                        testId={`mailer-sheet-sort-${col.key}`}
                      />
                    </span>
                  ) : col.label}
                </th>
              ))}
              <th style={{ width: 360 }} className="text-center px-2 py-3 font-semibold whitespace-nowrap">coverage</th>
            </tr>
          </thead>
          <tbody>
            {creators.map((c, rowIdx) => {
              const hasCoverage = getContentEntries(c).some((e) => (e.entry.url || '').trim());
              return (
                <tr key={c.id} className="hover:bg-pink-50/30 transition-colors" style={{ borderTop: '1px solid #f1f5f9' }} data-testid={`mailer-sheet-row-${c.id}`}>
                  <td className="bg-slate-50/40 text-center p-0">
                    <button
                      onClick={() => copyRow(c)}
                      title="copy this row"
                      className="w-full h-full flex items-center justify-center py-1.5 text-slate-400 hover:text-pink-600"
                      data-testid={`mailer-sheet-copyrow-${c.id}`}
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </td>
                  <td className="p-0 align-middle">
                    <div className="flex items-center h-full">
                      {!c.name && !c.ig_link && !c.tt_link ? (
                        <button
                          type="button"
                          onClick={() => setPickerForCreatorId(c.id)}
                          className="shrink-0 pl-1.5 text-slate-400 hover:text-pink-600"
                          title="add from database"
                          data-testid={`mailer-sheet-name-adddb-${c.id}`}
                        >
                          <Database className="w-3.5 h-3.5" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setProfileCreator(c)}
                          className="shrink-0 pl-1.5 text-slate-400 hover:text-pink-600"
                          title="view profile"
                          data-testid={`mailer-sheet-name-profile-${c.id}`}
                        >
                          <User className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <SheetCell value={c.name} onCommit={(v) => onPatch(c.id, { name: v })} testId={`mailer-sheet-name-${c.id}`} bold />
                    </div>
                  </td>
                  <td className="p-0 align-middle"><SheetCell value={c.ig_link} onCommit={(v) => onPatch(c.id, { ig_link: v })} testId={`mailer-sheet-ig-${c.id}`} tint="rose" align="left" showOpenLink /></td>
                  <td className="p-0 align-middle"><SheetCell value={c.ig_followers} type="number" align="right" onCommit={(v) => onPatch(c.id, { ig_followers: v === '' ? null : Number(v) })} testId={`mailer-sheet-igfollowers-${c.id}`} /></td>
                  <td className="p-0 align-middle"><SheetCell value={c.tt_link} onCommit={(v) => onPatch(c.id, { tt_link: v })} testId={`mailer-sheet-tt-${c.id}`} tint="violet" align="left" showOpenLink /></td>
                  <td className="p-0 align-middle"><SheetCell value={c.tt_followers} type="number" align="right" onCommit={(v) => onPatch(c.id, { tt_followers: v === '' ? null : Number(v) })} testId={`mailer-sheet-ttfollowers-${c.id}`} /></td>
                  <td className="p-0 align-middle"><SheetCell value={c.email} onCommit={(v) => onPatch(c.id, { email: v })} testId={`mailer-sheet-email-${c.id}`} tint="white" align="left" showCopy /></td>
                  <td className="p-0 align-middle"><SheetCheckCell checked={!!c.contacted} onToggle={() => onPatch(c.id, { contacted: !c.contacted })} color="#d394ab" testId={`mailer-sheet-contacted-${c.id}`} /></td>
                  <td className="p-0 align-middle"><SheetCheckCell checked={!!c.follow_up} onToggle={() => onPatch(c.id, { follow_up: !c.follow_up })} color="#e0a43b" testId={`mailer-sheet-followup-${c.id}`} /></td>
                  <td className="p-0 align-middle"><SheetCheckCell checked={!!c.product_sent} onToggle={() => onPatch(c.id, { product_sent: !c.product_sent })} color="#b16fa3" testId={`mailer-sheet-addresssent-${c.id}`} /></td>
                  <td className="p-0 align-middle"><SheetCheckCell checked={!!c.content_posted || hasCoverage} onToggle={() => onPatch(c.id, { content_posted: !c.content_posted })} color="#5fae8f" testId={`mailer-sheet-posted-${c.id}`} /></td>
                  <td className="p-0 align-middle"><SheetFeedbackCell checked={!!c.feedback_needed} text={c.feedback} onToggle={() => onPatch(c.id, { feedback_needed: !c.feedback_needed })} onCommitText={(v) => onPatch(c.id, { feedback: v })} testId={`mailer-sheet-feedback-${c.id}`} /></td>
                  <td className="px-2 py-2 align-middle">
                    <div className="flex items-center gap-3 overflow-x-auto">
                      {getContentEntries(c).map(({ arrKey, idx, entry }) => (
                        <div key={`${arrKey}-${idx}`} className="flex items-center gap-1 shrink-0">
                          <div className="w-28"><SheetCell value={entry.url} onCommit={(v) => onPatchContentLink(c, arrKey, idx, 'url', v)} tint="white" align="left" showOpenLink placeholder="link" testId={`mailer-sheet-contentlink-${idx}-${c.id}`} /></div>
                          <div className="w-14"><SheetCell value={entry.views} type="number" align="right" onCommit={(v) => onPatchContentLink(c, arrKey, idx, 'views', v === '' ? '' : Number(v))} placeholder="views" testId={`mailer-sheet-views-${idx}-${c.id}`} /></div>
                          <div className="w-14"><SheetCell value={entry.likes} type="number" align="right" onCommit={(v) => onPatchContentLink(c, arrKey, idx, 'likes', v === '' ? '' : Number(v))} placeholder="likes" testId={`mailer-sheet-likes-${idx}-${c.id}`} /></div>
                          <button onClick={() => removeCoverage(c, arrKey, idx)} className="shrink-0 text-slate-300 hover:text-red-500" title="remove coverage"><X className="w-3.5 h-3.5" /></button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addCoverage(c)}
                        className="inline-flex items-center gap-1 h-7 px-2 rounded-full text-[11px] font-medium text-pink-600 bg-pink-50 border border-pink-200 hover:bg-pink-100 shrink-0"
                        title="add a coverage link for this creator"
                        data-testid={`mailer-sheet-add-coverage-${c.id}`}
                      >
                        <Plus className="w-3 h-3" /> coverage
                      </button>
                      {(c.screenshots || []).map((url) => (
                        <div key={url} className="relative w-7 h-7 rounded-md overflow-hidden ring-1 ring-slate-200 group shrink-0">
                          <img src={url} alt="" className="w-full h-full object-cover" />
                          <button onClick={() => removeShot(c, url)} className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white" title="remove"><X className="w-3 h-3" /></button>
                        </div>
                      ))}
                      <label className="w-7 h-7 rounded-md border border-dashed border-pink-300 flex items-center justify-center cursor-pointer text-slate-400 hover:text-pink-600 hover:border-pink-400 shrink-0" title="upload screenshot">
                        {uploadingId === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { const files = Array.from(e.target.files || []); e.target.value = ''; uploadShots(c, files); }} data-testid={`mailer-sheet-screenshot-${c.id}`} />
                      </label>
                    </div>
                  </td>
                </tr>
              );
            })}
            {creators.length === 0 && (
              <tr><td colSpan={SHEET_BASE_COLUMNS.length + 2} className="text-center text-slate-400 text-xs py-8">no creators yet</td></tr>
            )}
          </tbody>
          {creators.length > 0 && (
            <tfoot className="bg-slate-50 text-slate-700 font-semibold text-center" style={{ borderTop: '2px solid #e2e8f0' }}>
              <tr>
                <td className="py-3"></td>
                <td className="py-3 pr-2 text-right text-[11px] uppercase tracking-widest text-slate-500">totals ({creators.length})</td>
                <td className="py-3"></td>
                <td className="py-3 text-xs">{creators.reduce((s, c) => s + (Number(c.ig_followers) || 0), 0).toLocaleString()}</td>
                <td className="py-3"></td>
                <td className="py-3 text-xs">{creators.reduce((s, c) => s + (Number(c.tt_followers) || 0), 0).toLocaleString()}</td>
                <td className="py-3"></td>
                <td className="py-3"></td>
                <td className="py-3"></td>
                <td className="py-3"></td>
                <td className="py-3"></td>
                <td className="py-3"></td>
                <td className="py-3"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {profileCreator && <CreatorProfileDialog creator={profileCreator} onClose={() => setProfileCreator(null)} />}
      {pickerForCreatorId && <SheetContactPickerDialog onPick={fillFromContact} onClose={() => setPickerForCreatorId(null)} campaignId={campaignId} clientId={clientId} excludeContactIds={excludeContactIds} />}
    </div>
  );
};


// Small profile-summary popup for a creator name click — shows every mailer they've been
// sent (all mailer_history entries) with the ones they've actually posted for highlighted.
const CreatorProfileDialog = ({ creator, onClose }) => {
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    if (!creator?.contact_id) { setContact(null); setLoading(false); return () => {}; }
    setLoading(true);
    api.get(`/database/contacts/${creator.contact_id}`)
      .then(({ data }) => { if (alive) setContact(data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [creator?.contact_id]);

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm p-0 overflow-hidden border-pink-200 shadow-2xl" data-testid="mailer-creator-profile-dialog">
        <DialogHeader className="sr-only"><DialogTitle>{creator.name || 'creator'} profile</DialogTitle><DialogDescription>mailer history for this creator</DialogDescription></DialogHeader>
        <div className="h-1.5 w-full" style={{ background: 'linear-gradient(90deg,#d394ab 0%,#e9c2d5 60%,#d3c1e1 100%)' }} />
        <div className="p-4">
          <div className="flex items-center gap-3">
            <span className="shrink-0 w-11 h-11 rounded-full overflow-hidden border border-pink-200 bg-pink-50 flex items-center justify-center">
              {creator.photo_url ? <img src={creator.photo_url} alt="" className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-pink-300" />}
            </span>
            <div className="min-w-0">
              <div className="font-semibold text-slate-800 truncate" data-testid="mailer-profile-name">{creator.name || 'unnamed creator'}</div>
              <div className="text-[11px] text-slate-500 truncate">{creator.email || '—'}</div>
            </div>
          </div>
          {loading ? (
            <div className="text-xs text-slate-400 mt-4 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> loading history…</div>
          ) : !creator.contact_id ? (
            <div className="text-xs text-slate-400 mt-4 italic">not yet tracked in the contacts database</div>
          ) : (
            <div className="mt-4 space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-medium">mailer history</div>
              {(contact?.mailer_history || []).length === 0 && <div className="text-xs text-slate-400 italic">no mailers yet</div>}
              <div className="flex flex-wrap gap-1.5">
                {(contact?.mailer_history || []).map((h, idx) => (
                  <span
                    key={`${h.campaign_id}-${idx}`}
                    className={`inline-flex items-center h-6 px-2.5 rounded-full text-[11px] font-semibold ${h.content_posted ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}
                    data-testid={`mailer-profile-history-${h.campaign_id}`}
                  >
                    {h.content_posted ? 'posted' : 'sent'}: {h.campaign_name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Single-select contact picker for an empty sheet row's name cell — fills that exact row's
// fields from an existing database contact instead of inserting a brand-new creator row.
const PICKER_FILTERS = [
  { key: 'posted_any', label: 'posted any mailer' },
  { key: 'posted_this_client', label: 'posted this client (another mailer)' },
  { key: 'sent_this_client', label: 'sent this client' },
  { key: 'sent_other_client', label: 'sent another client' },
  { key: 'not_sent_this_client', label: 'not sent this client' },
];

// Shared by SheetContactPickerDialog (fill-one-row) and AddFromDatabaseButton (bulk-add) so
// both "add from database" entry points offer the exact same filter behaviour. Checked
// filters now AND together (narrow, not widen) — e.g. posted_any + not_sent_this_client
// means "posted a mailer, but never for this client" i.e. posted for another client.
const pickerMatchesFilter = (c, key, clientId, campaignId) => {
  const history = c.mailer_history || [];
  if (key === 'posted_any') return history.some((h) => h.content_posted);
  if (key === 'posted_this_client') return history.some((h) => h.client_id === clientId && h.campaign_id !== campaignId && h.content_posted);
  if (key === 'sent_this_client') return history.some((h) => h.client_id === clientId);
  if (key === 'sent_other_client') return history.some((h) => h.client_id && h.client_id !== clientId);
  if (key === 'not_sent_this_client') return !history.some((h) => h.client_id === clientId);
  return false;
};

// A contact is a "duplicate" if another contact in the same database shares its ig link or
// tt link — see `computeDuplicateIds` import (shared with the Database page).

const sortContactsByName = (arr) => [...arr].sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));

// Small reusable filter-pill row shown inside both "add from database" dialogs.
const PickerFilterBar = ({ activeFilters, onToggle }) => (
  <div className="flex flex-wrap gap-1.5">
    {PICKER_FILTERS.map((f) => (
      <button
        key={f.key}
        type="button"
        onClick={() => onToggle(f.key)}
        className={`inline-flex items-center h-6 px-2 rounded-full text-[10px] font-medium border transition-colors ${activeFilters.includes(f.key) ? 'border-pink-400 bg-pink-50 text-pink-700' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}
        data-testid={`mailer-sheet-picker-filter-${f.key}`}
      >
        {f.label}
      </button>
    ))}
    {activeFilters.length > 0 && (
      <button type="button" onClick={() => onToggle(null)} className="text-[10px] text-slate-400 hover:text-pink-600" data-testid="mailer-sheet-picker-filter-clear">clear</button>
    )}
  </div>
);

// Small "N contacts in database" header line + a red "duplicate" tag for flagged rows.
const PickerCountLine = ({ count, testId }) => (
  <div className="text-[11px] text-slate-400" data-testid={testId}>{count} creator{count === 1 ? '' : 's'} in the full database</div>
);
const DuplicateTag = () => <span className="shrink-0 text-[9px] font-bold uppercase tracking-wide text-red-600 bg-red-50 border border-red-200 rounded px-1 py-0.5">duplicate</span>;


const SheetContactPickerDialog = ({ onPick, onClose, campaignId, clientId, excludeContactIds }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [activeFilters, setActiveFilters] = useState([]);
  useEffect(() => {
    api.get('/database/contacts').then(({ data }) => setContacts(data || [])).catch(() => toast.error('failed to load contacts')).finally(() => setLoading(false));
  }, []);
  const duplicateIds = useMemo(() => computeDuplicateIds(contacts), [contacts]);
  const toggleFilter = (key) => setActiveFilters((f) => (key === null ? [] : f.includes(key) ? f.filter((k) => k !== key) : [...f, key]));
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    const arr = contacts.filter((c) => {
      if (excludeContactIds?.has(c.id)) return false;
      if (t && ![c.name, c.handle, c.company].filter(Boolean).some((v) => v.toLowerCase().includes(t))) return false;
      if (activeFilters.length && !activeFilters.every((key) => pickerMatchesFilter(c, key, clientId, campaignId))) return false;
      return true;
    });
    return sortContactsByName(arr);
  }, [contacts, q, activeFilters, clientId, campaignId, excludeContactIds]);
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="mailer-sheet-contact-picker-dialog">
        <DialogHeader><DialogTitle>fill this row from the database</DialogTitle><DialogDescription className="sr-only">pick an existing contact to fill this creator row</DialogDescription></DialogHeader>
        <PickerCountLine count={contacts.length} testId="mailer-sheet-picker-count" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="search name, handle, company…" className="h-9 text-xs" data-testid="mailer-sheet-picker-search" />
        <PickerFilterBar activeFilters={activeFilters} onToggle={toggleFilter} />
        <div className="max-h-72 overflow-y-auto space-y-1 mt-2">
          {loading ? (
            <div className="text-center text-xs text-slate-400 py-6 flex items-center justify-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-xs text-slate-400 py-6">no contacts found</div>
          ) : filtered.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onPick(c)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-pink-50 text-left"
              data-testid={`mailer-sheet-picker-contact-${c.id}`}
            >
              {c.photo_url ? (
                <img src={c.photo_url} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
              ) : (
                <span className="w-6 h-6 rounded-full bg-pink-50 flex items-center justify-center shrink-0"><User className="w-3 h-3 text-pink-300" /></span>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-slate-700 truncate">{c.name}</div>
                <div className="text-[10px] text-slate-400 truncate">{[c.handle, c.company].filter(Boolean).join(' · ')}</div>
              </div>
              {duplicateIds.has(c.id) && <DuplicateTag />}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const MailerKanbanBoard = ({ creators, onCardClick, onMoveStage }) => {
  const [dragging, setDragging] = useState(null);
  return (
    <div className="overflow-x-auto pb-4" data-testid="mailer-kanban-board">
      <div className="flex gap-3 w-full">
        {KANBAN_COLUMNS.map((col) => (
          <MailerKanbanColumn
            key={col.key}
            col={col}
            creators={creators.filter((c) => kanbanStageOf(c) === col.key)}
            onDrop={(colKey) => { if (dragging) onMoveStage(dragging, colKey); setDragging(null); }}
            dragging={dragging}
            setDragging={setDragging}
            onCardClick={onCardClick}
          />
        ))}
      </div>
    </div>
  );
};


const MailerDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [client, setClient] = useState(null);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addCount, setAddCount] = useState(1);
  const [pasteText, setPasteText] = useState('');
  const [adding, setAdding] = useState(false);
  const [addSectionOpen, setAddSectionOpen] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilters, setStageFilters] = useState({ contacted: 'any', address_received: 'any', product_sent: 'any', product_received: 'any', content_posted: 'any' });
  const [viewMode, setViewMode] = useState('list');
  const [kanbanCreatorId, setKanbanCreatorId] = useState(null);
  const [productInput, setProductInput] = useState('');

  const load = async () => {
    try {
      const { data: camp } = await api.get(`/mailers/campaigns/${id}`);
      setCampaign(camp);
      const [{ data: cl }, { data: crs }] = await Promise.all([
        api.get('/clients'),
        api.get(`/mailers/campaigns/${id}/creators`),
      ]);
      setClient((cl || []).find((c) => c.id === camp.client_id) || null);
      setCreators(crs || []);
    } catch { toast.error('failed to load campaign'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const updateCampaign = async (patch) => {
    try {
      const { data } = await api.patch(`/mailers/campaigns/${id}`, patch);
      setCampaign((c) => ({ ...c, ...data }));
    } catch { toast.error('update failed'); }
  };

  const shareCampaign = async () => {
    try {
      const { data } = await api.post(`/mailers/campaigns/${id}/share`);
      const url = `${window.location.origin}/share/mailers/${data.token}`;
      const copied = await copyToClipboard(url);
      if (copied) toast.success('view-only link copied — no login needed');
      else toast.success('view-only link ready', { description: url, duration: 15000 });
    } catch { toast.error('failed to create share link'); }
  };

  const deleteCampaign = async () => {
    if (!window.confirm('delete this whole mailer campaign? this removes all its creators too.')) return;
    try {
      await api.delete(`/mailers/campaigns/${id}`);
      toast.success('campaign deleted');
      nav('/mailers');
    } catch { toast.error('failed to delete campaign'); }
  };

  const [backfilling, setBackfilling] = useState(false);
  const backfillProfiles = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post('/mailers/backfill-profiles', null, { params: { campaign_id: id } });
      toast.success(`checked ${data.checked} creators, filled in ${data.updated}`);
      const { data: fresh } = await api.get(`/mailers/campaigns/${id}/creators`);
      setCreators(fresh);
    } catch { toast.error('backfill failed'); }
    finally { setBackfilling(false); }
  };

  const addBlankRows = async () => {
    setAdding(true);
    try {
      const { data } = await api.post(`/mailers/campaigns/${id}/creators`, { count: Number(addCount) || 1 });
      setCreators(data || []);
      toast.success('creators added');
    } catch (e) { toast.error(e?.response?.data?.detail || 'failed to add'); }
    finally { setAdding(false); }
  };

  const addFromPaste = async () => {
    const links = pasteText.split('\n').map((l) => l.trim()).filter(Boolean);
    if (!links.length) { toast.error('paste at least one link'); return; }
    setAdding(true);
    try {
      const { data } = await api.post(`/mailers/campaigns/${id}/creators`, { links });
      setCreators(data || []);
      setPasteText('');
      toast.success(`${links.length} creator${links.length === 1 ? '' : 's'} added`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'failed to add'); }
    finally { setAdding(false); }
  };

  const patchCreator = async (creatorId, patch) => {
    setCreators((cs) => cs.map((c) => c.id === creatorId ? { ...c, ...patch } : c));
    try {
      const { data } = await api.patch(`/mailers/creators/${creatorId}`, patch);
      setCreators((cs) => cs.map((c) => c.id === creatorId ? { ...data, ...patch } : c));
    } catch { toast.error('save failed'); }
  };

  // Fetched profile data (followers/photo) is applied straight to the creators list — never
  // gets reverted by a slower in-flight PATCH landing after it (see race-condition note below).
  const applyFetchedProfile = (creatorId, fetchedCreator) => {
    setCreators((cs) => cs.map((c) => c.id === creatorId ? fetchedCreator : c));
  };

  const removeCreator = async (creatorId) => {
    try {
      await api.delete(`/mailers/creators/${creatorId}`);
      setCreators((cs) => cs.filter((c) => c.id !== creatorId));
    } catch { toast.error('delete failed'); }
  };

  // Sheet view's flat "content link/views/likes" column groups write into the matching index
  // of whichever array (ig_content_links / tt_content_links) that cell belongs to.
  const patchContentLink = (creator, arrKey, idx, field, value) => {
    const arr = [...(creator[arrKey] || [])];
    const existing = arr[idx] || { url: '', likes: '', views: '' };
    arr[idx] = { ...existing, [field]: value };
    patchCreator(creator.id, { [arrKey]: arr });
  };

  const cycleStage = (key) => {
    setStageFilters((f) => ({ ...f, [key]: f[key] === 'any' ? 'yes' : f[key] === 'yes' ? 'no' : 'any' }));
  };

  // Sort applies to list + sheet view only — grid/kanban already groups by stage column.
  const [sortBy, setSortBy] = useState(null); // null | 'name' | 'ig_followers' | 'tt_followers'
  const [sortDir, setSortDir] = useState('asc');
  const toggleSort = (key) => {
    if (sortBy !== key) { setSortBy(key); setSortDir(key === 'name' ? 'asc' : 'desc'); }
    else setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
  };

  const activeCreators = useMemo(() => creators.filter((c) => (c.status || 'active') === 'active'), [creators]);
  // Contacts already tied to a row on this mailer (any status) — excluded from the "add
  // from database" picker's search results so you never re-add someone already on it.
  const existingContactIds = useMemo(() => new Set(creators.map((c) => c.contact_id).filter(Boolean)), [creators]);
  const declinedCreators = useMemo(() => creators.filter((c) => c.status === 'declined'), [creators]);
  const backupCreators = useMemo(() => creators.filter((c) => c.status === 'backup'), [creators]);

  const summary = useMemo(() => {
    const contacted = activeCreators.filter((c) => c.contacted).length;
    const posted = activeCreators.filter((c) => c.content_posted).length;
    const gmv = activeCreators.reduce((s, c) => s + (c.tt_content_links || []).reduce((s2, l) => s2 + (Number(l.gmv) || 0), 0), 0);
    const posts = activeCreators.reduce((s, c) => s + (c.tt_content_links || []).filter((l) => (l.url || '').trim()).length, 0);
    const totalCost = activeCreators.reduce((s, c) => s + (Number(c.rate) || 0), 0);
    const deliverables = activeCreators.reduce((s, c) => s + (c.deliverable_items || []).length, 0);
    const pct = activeCreators.length ? Math.round((posted / activeCreators.length) * 100) : 0;
    const igFollowers = activeCreators.reduce((s, c) => s + (Number(c.ig_followers) || 0), 0);
    const ttFollowers = activeCreators.reduce((s, c) => s + (Number(c.tt_followers) || 0), 0);
    return { total: activeCreators.length, contacted, posted, gmv, posts, totalCost, deliverables, pct, igFollowers, ttFollowers };
  }, [activeCreators]);

  const filteredCreators = useMemo(() => {
    return activeCreators.filter((c) => {
      if (search && !(c.name || '').toLowerCase().includes(search.toLowerCase())) return false;
      for (const { key } of STAGE_FILTERS) {
        const want = stageFilters[key];
        if (want === 'any') continue;
        const has = !!c[key];
        if (want === 'yes' && !has) return false;
        if (want === 'no' && has) return false;
      }
      return true;
    });
  }, [activeCreators, search, stageFilters]);

  // Grid/kanban view only respects the name search — the stage filter pills are redundant
  // there since the columns already split creators by stage.
  const gridCreators = useMemo(() => {
    return activeCreators.filter((c) => !search || (c.name || '').toLowerCase().includes(search.toLowerCase()));
  }, [activeCreators, search]);

  // List + sheet view's final render order — sort is layered on top of the search/stage
  // filters above without changing the "X of Y" count they report.
  const sortedCreators = useMemo(() => {
    if (!sortBy) return filteredCreators;
    const arr = [...filteredCreators];
    arr.sort((a, b) => {
      let av, bv;
      if (sortBy === 'name') { av = (a.name || '').toLowerCase(); bv = (b.name || '').toLowerCase(); }
      else { av = Number(a[sortBy]) || 0; bv = Number(b[sortBy]) || 0; }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filteredCreators, sortBy, sortDir]);

  const moveStage = (creatorId, colKey) => patchCreator(creatorId, kanbanFlagsForColumn(colKey));
  const kanbanCreator = creators.find((c) => c.id === kanbanCreatorId) || null;

  const promoteToActive = (creatorId) => { patchCreator(creatorId, { status: 'active' }); toast.success('added to the campaign'); };

  const [pasteTextBackup, setPasteTextBackup] = useState('');
  const [addingBackup, setAddingBackup] = useState(false);
  const addBackupFromPaste = async () => {
    const links = pasteTextBackup.split('\n').map((l) => l.trim()).filter(Boolean);
    if (!links.length) { toast.error('paste at least one name or link'); return; }
    setAddingBackup(true);
    try {
      const { data } = await api.post(`/mailers/campaigns/${id}/creators`, { links, status: 'backup' });
      setCreators(data || []);
      setPasteTextBackup('');
      toast.success(`${links.length} backup creator${links.length === 1 ? '' : 's'} added`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'failed to add'); }
    finally { setAddingBackup(false); }
  };

  if (loading) return <div className="p-8 text-sm text-slate-400">loading…</div>;
  if (!campaign) return <div className="p-8 text-sm text-slate-400">campaign not found</div>;

  const isAffiliate = campaign.type === 'affiliate';
  const isPaid = campaign.type === 'paid';

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-emoji-skip="true">
      <Link to="/mailers" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4">
        <ArrowLeft className="w-4 h-4" /> mailers
      </Link>

      <div className="bg-gradient-to-br from-pink-50 to-white border border-pink-200 rounded-2xl p-5 mb-5">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <Input
              value={campaign.name}
              onChange={(e) => setCampaign((c) => ({ ...c, name: e.target.value }))}
              onBlur={(e) => updateCampaign({ name: e.target.value })}
              className="text-lg font-semibold border-none px-0 h-auto focus-visible:ring-0 bg-transparent"
              data-testid="mailer-campaign-name-input"
            />
            <div className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
              <span>{client?.logo}</span><span>{client?.name || 'unknown client'}</span>
              <span className="text-slate-300">·</span>
              <span>{isAffiliate ? <ShoppingBag className="w-3 h-3 inline mr-1" /> : isPaid ? <Wallet className="w-3 h-3 inline mr-1" /> : <Gift className="w-3 h-3 inline mr-1" />}{isAffiliate ? 'tts affiliate' : isPaid ? 'paid campaign' : 'organic mailer'}</span>
            </div>
          </div>
          <Select value={campaign.status} onValueChange={(v) => updateCampaign({ status: v })}>
            <SelectTrigger className="w-36 h-9 text-xs bg-white" data-testid="mailer-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex items-center gap-4 text-xs text-slate-600 border-l border-pink-200 pl-4">
            <div><div className="font-semibold text-slate-900">{summary.total}</div><div className="text-slate-400">creators</div></div>
            <div data-testid="mailer-total-ig-followers"><div className="font-semibold text-slate-900">{summary.igFollowers.toLocaleString()}</div><div className="text-slate-400">total ig followers</div></div>
            <div data-testid="mailer-total-tt-followers"><div className="font-semibold text-slate-900">{summary.ttFollowers.toLocaleString()}</div><div className="text-slate-400">total tt followers</div></div>
            {isPaid ? (
              <div data-testid="mailer-deliverables-total"><div className="font-semibold text-slate-900">{summary.deliverables}</div><div className="text-slate-400">deliverables</div></div>
            ) : (
              <div><div className="font-semibold text-slate-900">{summary.contacted}</div><div className="text-slate-400">contacted</div></div>
            )}
            <div data-testid="mailer-pct-posted"><div className="font-semibold text-pink-600">{summary.pct}%</div><div className="text-slate-400">posted</div></div>
            {isAffiliate && <div><div className="font-semibold text-slate-900">£{summary.gmv.toLocaleString()}</div><div className="text-slate-400">gmv</div></div>}
            {isAffiliate && <div data-testid="mailer-posts-count"><div className="font-semibold text-slate-900">{summary.posts}</div><div className="text-slate-400">posts</div></div>}
            {isPaid && <div data-testid="mailer-total-cost"><div className="font-semibold text-slate-900">£{summary.totalCost.toLocaleString()}</div><div className="text-slate-400">total cost</div></div>}
          </div>
          <button
            onClick={shareCampaign}
            className="inline-flex items-center gap-1 px-2.5 h-8 rounded-md text-[11px] text-slate-500 hover:text-pink-600 hover:bg-pink-50 border border-slate-200"
            data-testid="mailer-share-campaign"
            title="copy view-only link (no login needed)"
          >
            <Share2 className="w-3.5 h-3.5" /> share
          </button>
          <button
            onClick={backfillProfiles}
            disabled={backfilling}
            className="inline-flex items-center gap-1 px-2.5 h-8 rounded-md text-[11px] text-slate-500 hover:text-lilac-600 hover:bg-lilac-50 border border-slate-200 disabled:opacity-50"
            data-testid="mailer-backfill-profiles"
            title="re-check every creator's ig/tt link and fill in any missing photo or follower count"
          >
            {backfilling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} backfill profiles
          </button>
          <button
            onClick={deleteCampaign}
            className="inline-flex items-center gap-1 px-2.5 h-8 rounded-md text-[11px] text-slate-500 hover:text-red-600 hover:bg-red-50 border border-slate-200"
            data-testid="mailer-delete-campaign"
            title="delete this campaign"
          >
            <Trash2 className="w-3.5 h-3.5" /> delete
          </button>
        </div>
        <div className="mt-4 pt-4 border-t border-pink-200 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-pink-500 font-semibold flex items-center gap-1"><FileText className="w-3 h-3" /> copy document</label>
            <div className="mt-1 flex items-center gap-1.5">
              <Input
                value={campaign.copy_doc_link || ''}
                onChange={(e) => setCampaign((c) => ({ ...c, copy_doc_link: e.target.value }))}
                onBlur={(e) => updateCampaign({ copy_doc_link: e.target.value })}
                placeholder="paste link to the copy doc…"
                className="h-9 text-xs bg-white"
                data-testid="mailer-copy-doc-link"
              />
              {campaign.copy_doc_link && (
                <a href={campaign.copy_doc_link} target="_blank" rel="noopener noreferrer" className="shrink-0 h-9 w-9 flex items-center justify-center rounded-md border border-pink-200 text-slate-400 hover:text-pink-600 hover:border-pink-400" title="open copy doc" data-testid="mailer-copy-doc-open"><ExternalLink className="w-4 h-4" /></a>
              )}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-pink-500 font-semibold flex items-center gap-1"><ListChecks className="w-3 h-3" /> external list</label>
            <div className="mt-1 flex items-center gap-1.5">
              <Input
                value={campaign.external_list_link || ''}
                onChange={(e) => setCampaign((c) => ({ ...c, external_list_link: e.target.value }))}
                onBlur={(e) => updateCampaign({ external_list_link: e.target.value })}
                placeholder="paste link to an external list…"
                className="h-9 text-xs bg-white"
                data-testid="mailer-external-list-link"
              />
              {campaign.external_list_link && (
                <a href={campaign.external_list_link} target="_blank" rel="noopener noreferrer" className="shrink-0 h-9 w-9 flex items-center justify-center rounded-md border border-pink-200 text-slate-400 hover:text-pink-600 hover:border-pink-400" title="open external list" data-testid="mailer-external-list-open"><ExternalLink className="w-4 h-4" /></a>
              )}
            </div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-pink-200" data-testid="mailer-products-editor">
          <label className="text-[10px] uppercase tracking-widest text-pink-500 font-semibold flex items-center gap-1"><Gift className="w-3 h-3" /> products</label>
          <p className="text-[10px] text-slate-400 mb-1.5">add product options — each creator can be assigned one from a dropdown</p>
          <div className="flex flex-wrap items-center gap-1.5">
            {(campaign.products || []).map((p, i) => (
              <span key={`${p}-${i}`} className="inline-flex items-center gap-1 h-7 px-3 rounded-full text-[12px] font-semibold bg-pink-100 text-pink-700 border border-pink-300" data-testid={`mailer-product-bubble-${i}`}>
                {p}
                <button type="button" onClick={() => { const next = (campaign.products || []).filter((_, idx) => idx !== i); setCampaign((c) => ({ ...c, products: next })); updateCampaign({ products: next }); }} className="opacity-60 hover:opacity-100" data-testid={`mailer-product-remove-${i}`}>×</button>
              </span>
            ))}
            <Input
              value={productInput}
              onChange={(e) => setProductInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const t = productInput.trim();
                  if (!t || (campaign.products || []).includes(t)) { setProductInput(''); return; }
                  const next = [...(campaign.products || []), t];
                  setCampaign((c) => ({ ...c, products: next }));
                  updateCampaign({ products: next });
                  setProductInput('');
                }
              }}
              placeholder="add product + enter"
              className="h-7 text-[12px] w-40 border-dashed bg-white"
              data-testid="mailer-product-input"
            />
          </div>
        </div>
      </div>

      <div className="bg-white border border-pink-100 rounded-2xl p-3 mb-4 flex items-center gap-2 flex-wrap" data-testid="mailer-search-filter-bar">
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="search creator name…" className="h-8 text-xs pl-8 w-48" data-testid="mailer-search-input" />
        </div>
        <div className="w-px h-5 bg-slate-200" />
        <div className="inline-flex items-center rounded-md border border-slate-200 overflow-hidden">
          <button
            onClick={() => setViewMode('list')}
            className={`px-2.5 h-8 text-[11px] font-medium transition-colors ${viewMode === 'list' ? 'bg-pink-500 text-white' : 'text-slate-500 hover:bg-slate-50'}`}
            data-testid="mailer-view-list-btn"
          >
            cards
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={`px-2.5 h-8 text-[11px] font-medium transition-colors ${viewMode === 'grid' ? 'bg-pink-500 text-white' : 'text-slate-500 hover:bg-slate-50'}`}
            data-testid="mailer-view-grid-btn"
            title="kanban board — drag creators between stages"
          >
            board
          </button>
          <button
            onClick={() => setViewMode('sheet')}
            className={`px-2.5 h-8 text-[11px] font-medium transition-colors ${viewMode === 'sheet' ? 'bg-pink-500 text-white' : 'text-slate-500 hover:bg-slate-50'}`}
            data-testid="mailer-view-sheet-btn"
            title="spreadsheet list — editable, exportable to Google Sheets"
          >
            list
          </button>
        </div>
        <div className="w-px h-5 bg-slate-200" />
        <AddFromDatabaseButton campaignId={id} clientId={campaign.client_id} excludeContactIds={existingContactIds} onAdded={(rows) => setCreators(rows)} />
        <ImportSpreadsheetButton campaignId={id} onImported={(rows) => setCreators(rows)} />
        {(viewMode === 'list' || viewMode === 'sheet') && (
          <>
            <div className="w-px h-5 bg-slate-200" />
            {STAGE_FILTERS.map((f) => (
              <FilterPill key={f.key} label={f.label} state={stageFilters[f.key]} onCycle={() => cycleStage(f.key)} testId={`mailer-filter-${f.key}`} />
            ))}
            {(search || Object.values(stageFilters).some((v) => v !== 'any')) && (
              <button
                onClick={() => { setSearch(''); setStageFilters({ contacted: 'any', address_received: 'any', product_sent: 'any', product_received: 'any', content_posted: 'any' }); }}
                className="text-[11px] text-slate-400 hover:text-pink-600 ml-1"
                data-testid="mailer-clear-filters"
              >
                clear
              </button>
            )}
            {viewMode === 'list' && (
              <>
                <div className="w-px h-5 bg-slate-200" />
                <span className="text-[10px] uppercase tracking-widest text-slate-400 font-medium">sort</span>
                {[{ key: 'name', label: 'name' }, { key: 'ig_followers', label: 'ig followers' }, { key: 'tt_followers', label: 'tt followers' }].map((s) => (
                  <button
                    key={s.key}
                    onClick={() => toggleSort(s.key)}
                    className={`inline-flex items-center gap-1 h-7 px-2 rounded-full text-[11px] font-medium border transition-colors ${sortBy === s.key ? 'border-pink-400 bg-pink-50 text-pink-700' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                    data-testid={`mailer-sort-${s.key}-btn`}
                  >
                    {s.label}
                    {sortBy === s.key ? (sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                  </button>
                ))}
              </>
            )}
            <span className="ml-auto text-[11px] text-slate-400">{filteredCreators.length} of {activeCreators.length}</span>
          </>
        )}
      </div>

      {viewMode === 'grid' ? (
        activeCreators.length === 0 ? (
          <div className="bg-white border border-dashed border-pink-200 rounded-2xl p-10 text-center text-sm text-slate-400">no creators added yet</div>
        ) : (
          <MailerKanbanBoard creators={gridCreators} onCardClick={(c) => setKanbanCreatorId(c.id)} onMoveStage={moveStage} />
        )
      ) : viewMode === 'sheet' ? (
        activeCreators.length === 0 ? (
          <div className="bg-white border border-dashed border-pink-200 rounded-2xl p-10 text-center text-sm text-slate-400">no creators added yet</div>
        ) : filteredCreators.length === 0 ? (
          <div className="bg-white border border-dashed border-pink-200 rounded-2xl p-10 text-center text-sm text-slate-400">no creators match these filters</div>
        ) : (
          <MailerSheetView creators={sortedCreators} isAffiliate={isAffiliate} onPatch={patchCreator} onPatchContentLink={patchContentLink} sortBy={sortBy} sortDir={sortDir} onToggleSort={toggleSort} campaignId={id} clientId={campaign.client_id} excludeContactIds={existingContactIds} title={campaign.name} />
        )
      ) : (
        <div className="space-y-2.5">
          {activeCreators.length === 0 ? (
            <div className="bg-white border border-dashed border-pink-200 rounded-2xl p-10 text-center text-sm text-slate-400">no creators added yet</div>
          ) : filteredCreators.length === 0 ? (
            <div className="bg-white border border-dashed border-pink-200 rounded-2xl p-10 text-center text-sm text-slate-400">no creators match these filters</div>
          ) : (
            sortedCreators.map((c) => (
              <MailerCreatorCard key={c.id} creator={c} isAffiliate={isAffiliate} isPaid={isPaid} onPatch={(patch) => patchCreator(c.id, patch)} onFetched={(fetched) => applyFetchedProfile(c.id, fetched)} onRemove={() => removeCreator(c.id)} campaignId={id} clientId={campaign.client_id} excludeContactIds={existingContactIds} products={campaign.products || []} />
            ))
          )}
        </div>
      )}

      <Dialog open={!!kanbanCreator} onOpenChange={(v) => { if (!v) setKanbanCreatorId(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="mailer-kanban-detail-dialog">
          <DialogHeader className="sr-only"><DialogTitle>edit creator</DialogTitle><DialogDescription>edit this mailer creator's details</DialogDescription></DialogHeader>
          {kanbanCreator && (
            <MailerCreatorCard
              creator={kanbanCreator}
              isAffiliate={isAffiliate}
              isPaid={isPaid}
              onPatch={(patch) => patchCreator(kanbanCreator.id, patch)}
              onFetched={(fetched) => applyFetchedProfile(kanbanCreator.id, fetched)}
              onRemove={() => { removeCreator(kanbanCreator.id); setKanbanCreatorId(null); }}
              campaignId={id}
              clientId={campaign.client_id}
              excludeContactIds={existingContactIds}
            />
          )}
        </DialogContent>
      </Dialog>

      <div className="bg-gradient-to-br from-pink-50 to-white border border-pink-200 rounded-2xl p-5 mt-5 mb-5">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-400 font-medium">notes</label>
          <Textarea
            value={campaign.notes || ''}
            onChange={(e) => setCampaign((c) => ({ ...c, notes: e.target.value }))}
            onBlur={(e) => updateCampaign({ notes: e.target.value })}
            rows={2}
            placeholder="anything worth flagging about this campaign — also shown on the public share link"
            className="mt-1.5 text-xs bg-white"
            data-testid="mailer-campaign-notes"
          />
        </div>
        <div className="flex items-center gap-3 mt-4 pt-4 border-t border-pink-100">
          <label className="text-[10px] uppercase tracking-widest text-slate-400 font-medium">active dates</label>
          <Input type="date" value={campaign.start_date || ''} onChange={(e) => setCampaign((c) => ({ ...c, start_date: e.target.value }))} onBlur={(e) => updateCampaign({ start_date: e.target.value })} className="h-8 text-xs w-40 bg-white" data-testid="mailer-start-date-input" />
          <span className="text-slate-400 text-xs">→</span>
          <Input type="date" value={campaign.end_date || ''} onChange={(e) => setCampaign((c) => ({ ...c, end_date: e.target.value }))} onBlur={(e) => updateCampaign({ end_date: e.target.value })} className="h-8 text-xs w-40 bg-white" data-testid="mailer-end-date-input" />
        </div>
      </div>

      <FolderSection
        title="declined"
        tone="declined"
        creators={declinedCreators}
        actionLabel="add back"
        onAction={promoteToActive}
        onRemove={removeCreator}
        testId="mailer-declined-folder"
      />
      {viewMode === 'list' && (
        <>
          <FolderSection
            title="backup creators"
            tone="backup"
            creators={backupCreators}
            actionLabel="add to campaign"
            onAction={promoteToActive}
            onRemove={removeCreator}
            testId="mailer-backup-folder"
          />

          <div className="bg-gradient-to-br from-pink-50 to-white border border-pink-200 rounded-2xl p-4 mb-5">
            <button
              onClick={() => setAddSectionOpen((v) => !v)}
              className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-pink-600 font-semibold w-full"
              data-testid="mailer-add-section-toggle"
            >
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${addSectionOpen ? 'rotate-180' : ''}`} />
              add more creators
            </button>
            {addSectionOpen && (
              <>
                <div className="mt-3">
                  <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">paste names or links (one per line)</label>
                  <Textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} rows={3} placeholder={'Jane Doe\nhttps://tiktok.com/@creator1\nhttps://instagram.com/creator2'} className="mt-1.5 text-xs bg-white" data-testid="mailer-paste-links" />
                  <Button onClick={addFromPaste} disabled={adding} className="mt-2 h-8 text-xs bg-pink-500 hover:bg-pink-600 text-white" data-testid="mailer-add-from-paste-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> add from paste
                  </Button>
                </div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-pink-100">
                  <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">or add blank rows manually</label>
                  <NumberInput value={addCount} onChange={setAddCount} className="w-20 h-8 text-xs bg-white" data-testid="mailer-add-count-input" />
                  <Button onClick={addBlankRows} disabled={adding} className="h-8 text-xs bg-pink-500 hover:bg-pink-600 text-white" data-testid="mailer-add-rows-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> add rows
                  </Button>
                </div>
                <div className="mt-3 pt-3 border-t border-pink-100">
                  <p className="text-[11px] text-slate-400">looking to add from the database? that button now lives up top next to the view switcher.</p>
                </div>
              </>
            )}
          </div>

          <div className="bg-gradient-to-br from-lilac-50 to-white border border-lilac-200 rounded-2xl p-4 mt-5">
            <label className="text-[11px] uppercase tracking-widest text-lilac-700 font-semibold">add backup creators</label>
            <p className="text-xs text-slate-500 mt-1">these sit in the backup folder above and don't count toward the campaign total — quick-add one in whenever someone declines.</p>
            <Textarea value={pasteTextBackup} onChange={(e) => setPasteTextBackup(e.target.value)} rows={2} placeholder={'Jane Doe\nhttps://tiktok.com/@backup1'} className="mt-2 text-xs bg-white" data-testid="mailer-backup-paste" />
            <Button onClick={addBackupFromPaste} disabled={addingBackup} className="mt-2 h-8 text-xs bg-lilac-500 hover:bg-lilac-600 text-white" data-testid="mailer-add-backup-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> add as backup
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default MailerDetail;

